import { Record, List } from "immutable";
import { DateStringNumber, Nullable } from "common/records";
import { IProductOptionData, ProductOptionRecord } from "common/records/ProductOption";
import { IProductToOptionGroupLinkData } from "common/records/ProductToOptionGroupLink";
import { ImageRecord, IImageData } from "common/records/Image";
import { IDocumentData } from "common/records/Document";
import { AbleProp } from "common/types";
import { IMeasureValueRecord } from "common/records/MeasureValue";
import { ISkuData } from "common/records/Sku";
import { checkDate } from "common/helpers/Date";

export const productFromJSON = (json: Partial<IProductData>): ProductRecord => {
  const recordData: IProductRecord = { ...(json as any) };

  if (Array.isArray(json.option_groups)) {
    recordData.option_group_ids = List(json.option_groups);
  }
  if (json.created_at) {
    recordData.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    recordData.updated_at = checkDate(json.updated_at);
  }

  let record = new ProductRecord(recordData);

  if (json.ptog_links) {
    record = record.set("ptog_link_ids", List(json.ptog_links.map((p) => p.id)));
  }

  if (Array.isArray(json.children)) {
    record = record.set("children_ids", List(json.children.map((c) => c.id)));
  }

  if (json.images) {
    record = record.set("image_ids", List(json.images.map((i) => i.id)));
  }

  if (json.documents) {
    record = record.set("document_ids", List(json.documents.map((d) => d.id)));
  }
  if (json.sku) {
    record = record.set("sku_id", json.sku.id);
  }

  return record;
};

/** The opening fields bracket matching reads — satisfied by OpeningRecord/WindowRecord/DoorRecord. */
export interface IBracketOpening {
  width: number;
  height: number;
  ui: number;
  area: number;
}

/**
 * Width/height bounds gate: each bound applies only when set (> 0); max bounds are inclusive
 * (rsf-app Product.widthHeightMatch).
 *
 * @param {IBracketOpening} opening
 * @param {ProductRecord} product
 * @returns {boolean}
 */
export const widthHeightMatch = (opening: IBracketOpening, product: ProductRecord): boolean => {
  let match = true;
  if (product.min_width > 0) {
    match = match && opening.width >= product.min_width;
  }
  if (product.min_height > 0) {
    match = match && opening.height >= product.min_height;
  }
  if (product.max_width > 0) {
    match = match && opening.width <= product.max_width;
  }
  if (product.max_height > 0) {
    match = match && opening.height <= product.max_height;
  }
  return match;
};

// All four bounds always applied, half-open (>= min, < max) — rsf-app widthHeightBracketMatch.
const widthHeightBracketMatch = (opening: IBracketOpening, product: ProductRecord): boolean =>
  opening.width >= product.min_width &&
  opening.height >= product.min_height &&
  opening.width < product.max_width &&
  opening.height < product.max_height;

const bracketMatch = (opening: IBracketOpening, product: ProductRecord, bracketType: string): boolean => {
  if (bracketType === "width_height_bt") {
    return widthHeightBracketMatch(opening, product);
  }

  const measurement = bracketType === "sqft" ? opening.area : opening.ui;
  const match = measurement >= product.min_ui && measurement < product.max_ui;
  if (!match) {
    return match;
  }

  return widthHeightMatch(opening, product);
};

const bracket = (
  productChildren: List<ProductRecord>,
  opening: IBracketOpening,
  bracketType: string,
): ProductRecord | undefined => productChildren.find((product) => bracketMatch(opening, product, bracketType));

// per_ui/per_sqft parents delegate matching to each child's own bracket_type; per_ui/per_sqft
// children match on width/height bounds only (rsf-app Product.perUI).
const perUI = (productChildren: List<ProductRecord>, opening: IBracketOpening): ProductRecord | undefined =>
  productChildren.find((product) => {
    switch (product.bracket_type) {
      case "width_height_bt":
      case "ui_bt":
      case "sqft":
        return bracketMatch(opening, product, product.bracket_type);
      case "per_ui":
      case "per_sqft":
        return widthHeightMatch(opening, product);
      default:
        return bracketMatch(opening, product, "ui_bt");
    }
  });

/**
 * The child bracket product matching an opening under a window/door parent, per the parent's
 * `bracket_type` (`width_height_bt`/`ui_bt`/`sqft`/`per_ui`/`per_sqft`, unset → `ui_bt`); first
 * matching child wins. Ports rsf-app Product.findProductBracket.
 *
 * @param {ProductRecord} parentProduct
 * @param {List<ProductRecord>} productChildren The parent's child products
 * @param {IBracketOpening} opening
 * @returns {Nullable<ProductRecord>} the matched child, or null (also when the parent isn't a window/door)
 */
export const findProductBracket = (
  parentProduct: ProductRecord,
  productChildren: List<ProductRecord>,
  opening: IBracketOpening,
): Nullable<ProductRecord> => {
  if (parentProduct.kind !== "window" && parentProduct.kind !== "door") {
    return null;
  }

  let product: ProductRecord | undefined;
  switch (parentProduct.bracket_type) {
    case "width_height_bt":
    case "ui_bt":
    case "sqft":
      product = bracket(productChildren, opening, parentProduct.bracket_type);
      break;
    case "per_ui":
    case "per_sqft":
      product = perUI(productChildren, opening);
      break;
    default:
      product = bracket(productChildren, opening, "ui_bt");
  }

  return product ?? null;
};

export const hasSizes = (product: ProductRecord) => {
  return (
    product.min_ui > 0 ||
    product.max_ui > 0 ||
    product.min_width > 0 ||
    product.max_width > 0 ||
    product.min_height > 0 ||
    product.max_height > 0 ||
    product.kind === "window" ||
    product.kind === "door"
  );
};

export const uoms = List<string>([
  "ea",
  "sqft",
  "lft",
  "ui",
  "in",
  "sq",
  "pair",
  "sash",
  "mile",
  "gal",
  "layer",
  "hr",
  "cm",
  "m",
  "sqm",
  "km",
  "l",
  "percent",
  "all",
  "w",
  "kW",
  "day",
]);

export interface IProductData {
  classy: string;
  id: number;
  parent_id: number;
  price_list_id: number;
  parent: IProductData;
  children: IProductData[];
  images: IImageData[];
  documents: IDocumentData[];
  name: string;
  loading: boolean;
  include_parent_names: boolean;
  option_groups: number[];
  description: string;
  cost: number;
  msrp: number;
  price: number;
  product_price: number;
  labor_price: number;
  visibility: string;
  uom: string;
  sort_order: number;
  sku: ISkuData;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
  uuid: string;
  bracket_type: string;
  min_ui: number;
  max_ui: number;
  min_width: number;
  max_width: number;
  min_height: number;
  max_height: number;
  base_product_price: number;
  base_labor_price: number;
  base_measure: number;
  kind: string;
  standard_measurement: number;
  standard_uom: string;
  measurement_link: IMeasureValueRecord;
  size_html: string;
  doc_html: string;
  ptog_links: IProductToOptionGroupLinkData[];
  options?: IProductOptionData[];
  discountable?: AbleProp;
  parent_discountable?: AbleProp;
  markupable?: AbleProp;
  parent_markupable?: AbleProp;
  leaf: boolean;
}

export interface IProductRecord {
  classy: string;
  id: number;
  parent_id: Nullable<number>;
  price_list_id: Nullable<number>;
  children_ids: List<number>;
  image_ids: List<number>;
  document_ids: List<number>;
  images: List<ImageRecord>;
  name: string;
  loading: boolean;
  include_parent_names: boolean;
  ancestral_name: string;
  ancestral_name_lowercase: string;
  option_group_ids: List<number>;
  description: string;
  cost: number;
  msrp: number;
  price: number;
  product_price: number;
  labor_price: number;
  visibility: string;
  uom: string;
  sort_order: Nullable<number>;
  sku_id: Nullable<number>;
  created_at: Date;
  updated_at: Date;
  uuid: string;
  bracket_type: string;
  min_ui: number;
  max_ui: number;
  min_width: number;
  max_width: number;
  min_height: number;
  max_height: number;
  base_product_price: number;
  base_labor_price: number;
  base_measure: number;
  kind: string;

  standard_measurement: number;
  standard_uom: string;
  measurement_link: Nullable<IMeasureValueRecord>;
  size_html: string;
  doc_html: string;
  ptog_link_ids: List<number>;
  options?: List<ProductOptionRecord>;
  discountable?: AbleProp;
  parent_discountable?: AbleProp;
  markupable?: AbleProp;
  parent_markupable?: AbleProp;
  leaf: boolean;
}

const defaultProductProps: IProductRecord = {
  classy: "Product",
  id: 0,
  parent_id: null,
  price_list_id: null,
  children_ids: List<number>(),
  image_ids: List<number>(),
  document_ids: List<number>(),
  images: List<ImageRecord>(),
  name: "",
  loading: false,
  include_parent_names: true,
  ancestral_name: "",
  ancestral_name_lowercase: "",
  option_group_ids: List<number>(),
  description: "",
  cost: 0,
  msrp: 0,
  price: 0,
  product_price: 0,
  labor_price: 0,
  visibility: "always",
  uom: "ea",
  sort_order: null,
  sku_id: 0,
  created_at: new Date(),
  updated_at: new Date(),
  uuid: "",
  bracket_type: "unset_bt",
  min_ui: 0,
  max_ui: 0,
  min_width: 0,
  max_width: 0,
  min_height: 0,
  max_height: 0,
  base_product_price: 0,
  base_labor_price: 0,
  base_measure: 0,
  kind: "unset",

  standard_measurement: 0,
  standard_uom: "",
  measurement_link: null,
  size_html: "",
  doc_html: "",
  ptog_link_ids: List<number>(),
  options: List<ProductOptionRecord>(),
  discountable: "default",
  parent_discountable: "default",
  markupable: "default",
  parent_markupable: "default",
  leaf: false,
};

export class ProductRecord extends Record<IProductRecord>(defaultProductProps) implements IProductRecord {}

/**
 * Resolves the effective discountable value for a product considering parent inheritance.
 *
 * The discountable property uses string-based inheritance:
 * - "yes" or "no": explicit value, used directly
 * - "default" or null/undefined: inherit from parent_discountable
 *
 * Returns the resolved AbleProp value that should be used for line items.
 */
export const getEffectiveDiscountable = (product: ProductRecord): AbleProp => {
  // If product has explicit "yes" or "no", use it
  if (product.discountable === "yes" || product.discountable === "no") {
    return product.discountable;
  }

  // Otherwise inherit from parent_discountable
  // If parent_discountable is also "default" or unset, return "default"
  // (which will be treated as discountable=true by isDiscountable())
  return product.parent_discountable ?? "default";
};

/**
 * Resolves the effective markupable value for a product considering parent inheritance.
 * Same logic as getEffectiveDiscountable but for markupable property.
 */
export const getEffectiveMarkupable = (product: ProductRecord): AbleProp => {
  if (product.markupable === "yes" || product.markupable === "no") {
    return product.markupable;
  }
  return product.parent_markupable ?? "default";
};
